/**
 * « Cette valeur n'a pas la forme attendue », dit dans le back-office.
 *
 * RETOUR DU 08/09 : « en mode web, quand une valeur n'est pas dans le format
 * attendu, ajouter un petit point d'exclamation pour attirer l'oeil serait
 * parfait. Peut etre avec une petite info bulle : Format inattendu... ou
 * quelque chose d'explicite. »
 *
 * Sa capture montre le pave d'identite en LECTURE : « Telephone 07876 »,
 * « Email test ». Deux valeurs qui ont traverse la tablette, le reseau et
 * Firestore sans que personne ne les regarde, et qui s'affichent au
 * secretariat avec le meme aplomb qu'une valeur juste. C'est bien la lecture
 * qu'on outille ici, pas un formulaire de saisie : la secretaire ne les tape
 * pas, elle les recopie vers le SIL.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * CE FICHIER EST LE JUMEAU DE `mobile/utils/contactFormat.js` ET
 * `mobile/utils/dateNaissance.js`
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Memes regles, aux memes seuils. Un numero que la tablette accepte sans un
 * mot ne doit pas se faire signaler ici, et l'inverse encore moins : deux
 * ecrans qui jugent differemment la meme valeur apprennent surtout a ne plus
 * croire aucun des deux. Si une regle change d'un cote, elle change de
 * l'autre. C'est le meme arrangement que `documentNaming`, ecrit le 28/08
 * pour la meme raison.
 *
 * ON SIGNALE, ON NE CORRIGE PAS, ET ON NE BLOQUE RIEN. La valeur reste
 * affichee, copiable, exportable telle quelle. Un numero etranger, un nom a
 * particule, une date de centenaire : tout cela existe et doit passer.
 *
 * ET ON SE TAIT PLUTOT QUE DE CRIER AU LOUP. Il n'y a de regle que la ou une
 * forme est reellement attendue — telephone, e-mail, date, NIR, code INSEE,
 * sexe. Pas de regle sur un nom ni sur un prenom : personne ne sait a quoi
 * ressemble un nom, et un seul faux positif sur un patient bien saisi suffit
 * a ce qu'on cesse de regarder les points d'exclamation.
 */

/**
 * Ce qu'on accepte de juger.
 *
 * Les valeurs viennent de Firestore : un champ peut y etre absent, nul, ou --
 * accident deja vu -- un objet. `String({})` rend « [object Object] », qui
 * ressemble alors a une valeur fausse et allumerait un point d'exclamation
 * pour un defaut qui n'est pas celui du dossier. On ne juge donc que ce qui
 * est du texte ou un nombre ; le reste ne se signale pas.
 */
function texte(valeur) {
  if (typeof valeur === "number") return String(valeur);
  if (typeof valeur !== "string") return "";
  return valeur.trim();
}

/** Numero francais a dix chiffres, ou format international. */
export function telephonePlausible(valeur) {
  const chiffres = texte(valeur).replace(/[\s.\-()]/g, "");
  if (/^\+\d{8,15}$/.test(chiffres)) return true;
  return /^\d{10}$/.test(chiffres);
}

export function emailPlausible(valeur) {
  return /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(texte(valeur));
}

/** Une date n'existe que si son jour tient dans son mois. */
function dateExiste(jour, mois, annee) {
  const j = Number(jour);
  const m = Number(mois);
  const a = Number(annee);
  if (!j || !m || !a) return false;
  if (m < 1 || m > 12) return false;
  if (j < 1) return false;
  return j <= new Date(a, m, 0).getDate();
}

/**
 * La date de naissance telle qu'elle s'affiche ici : JJ/MM/AAAA.
 *
 * Le back-office lit ce que la tablette a ecrit. Une date qui arrive dans une
 * autre ecriture (AAAA-MM-JJ d'une ordonnance numerique, par exemple) est
 * acceptee sans un mot : le probleme serait alors dans la conversion, pas dans
 * la valeur, et ce n'est pas a la secretaire de le corriger.
 */
export function messageDateNaissance(valeur, aujourdhui = new Date()) {
  const v = texte(valeur);
  if (!v) return "";

  const m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) {
    // Une autre ecriture reconnaissable n'est pas une faute de saisie.
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return "";
    return "Format inattendu : une date de naissance s'écrit JJ/MM/AAAA.";
  }

  const [, jour, mois, annee] = m;
  const nMois = Number(mois);
  const nJour = Number(jour);
  const nAnnee = Number(annee);

  if (nMois < 1 || nMois > 12) {
    return `Format inattendu : le mois ${mois} n'existe pas.`;
  }
  if (nJour < 1) return "Format inattendu : le jour doit être supérieur à 00.";
  if (!dateExiste(jour, mois, annee)) {
    const dernier = new Date(nAnnee, nMois, 0).getDate();
    return `Format inattendu : le mois ${mois}/${annee} ne compte que ${dernier} jours.`;
  }
  if (new Date(nAnnee, nMois - 1, nJour) > aujourdhui) {
    return "Format inattendu : cette date de naissance est dans le futur.";
  }
  if (nAnnee < aujourdhui.getFullYear() - 130) {
    return `Format inattendu : l'année ${annee} donnerait plus de 130 ans.`;
  }
  return "";
}

/**
 * Le NIR : treize chiffres, ou quinze avec sa cle de controle.
 *
 * La cle EST verifiee quand elle est la — c'est tout l'interet d'en avoir une,
 * et c'est le seul champ de ce pave capable de dire « ce numero est faux »
 * plutot que « ce numero est etrange ». La Corse s'ecrit 2A/2B au lieu du
 * departement : la regle officielle est de les remplacer par 19 et 18 avant
 * le calcul.
 */
export function messageNir(valeur) {
  const brut = texte(valeur).toUpperCase().replace(/\s/g, "");
  if (!brut) return "";
  // Les deux controles sont dans cet ordre parce que leurs messages ne se
  // valent pas : « il y a une lettre la-dedans » et « il manque un chiffre »
  // n'appellent pas la meme correction, et un numero trop court compose
  // uniquement de chiffres ne doit pas s'entendre reprocher ses lettres.
  if (!/^[0-9AB]+$/.test(brut)) {
    return "Format inattendu : un NIR ne porte que des chiffres (2A/2B pour la Corse).";
  }
  if (brut.length !== 13 && brut.length !== 15) {
    return `Format inattendu : un NIR compte 13 ou 15 chiffres, celui-ci en compte ${brut.length}.`;
  }
  if (brut.length === 15) {
    const corps = brut.slice(0, 13).replace("2A", "19").replace("2B", "18");
    if (!/^\d{13}$/.test(corps)) return "";
    const cleAttendue = 97 - (Number(corps) % 97);
    const cleLue = Number(brut.slice(13, 15));
    if (cleAttendue !== cleLue) {
      return `Clé de contrôle incorrecte : ce NIR devrait finir par ${String(cleAttendue).padStart(2, "0")}.`;
    }
  }
  return "";
}

/** Le code commune INSEE : cinq caracteres, la Corse en 2A/2B. */
export function messageCodeInsee(valeur) {
  const v = texte(valeur).toUpperCase();
  if (!v) return "";
  if (!/^(\d{5}|2[AB]\d{3})$/.test(v)) {
    return "Format inattendu : un code INSEE compte 5 caractères (2A/2B pour la Corse).";
  }
  return "";
}

/**
 * Le sexe.
 *
 * CETTE REGLE A CRIE AU LOUP LE 08/09, ET IL FAUT QUE CA SE VOIE ICI.
 *
 * Ecrite le matin, elle n'acceptait que « M » et « F ». Or la tablette
 * enregistre « h » et « f » — c'est la valeur des boutons radio de l'accueil,
 * depuis toujours. Resultat : TOUT patient masculin s'affichait au secretariat
 * avec un point d'exclamation. Nicolas l'a vu dans l'heure : « peux tu regler
 * le probleme avec les hommes car le petit h n'est pas reconnu ».
 *
 * C'est exactement le defaut contre lequel l'en-tete de ce fichier met en
 * garde. La lecon n'est pas « ajouter h a la liste » : c'est qu'une regle de
 * format ne s'ecrit pas d'apres ce que le SIL attend en sortie, mais d'apres
 * ce que la base contient reellement. On reprend donc le MEME vocabulaire que
 * `normaliserSexe` de mobile/utils/patientMatcher.js, qui connait toutes les
 * ecritures rencontrees en production — la tablette, le Datamatrix INS (M/F),
 * l'ordonnance lue par l'IA (« Masculin »), et les codes NIR (1, 2).
 *
 * Ne reste signale que ce qu'aucune de ces ecritures ne couvre.
 */
const SEXES_CONNUS = new Set([
  "h", "m", "1", "3", "7", "homme", "masculin",
  "f", "2", "4", "8", "femme", "feminin", "féminin",
]);

export function messageSexe(valeur) {
  const v = texte(valeur).toLowerCase();
  if (!v) return "";
  if (SEXES_CONNUS.has(v)) return "";
  return `Format inattendu : « ${valeur} » n'est pas un sexe reconnu.`;
}

export function messageTelephone(valeur) {
  if (!texte(valeur)) return "";
  if (telephonePlausible(valeur)) return "";
  return "Format inattendu : ce numéro ne ressemble pas à un numéro de téléphone.";
}

export function messageEmail(valeur) {
  if (!texte(valeur)) return "";
  if (emailPlausible(valeur)) return "";
  return "Format inattendu : cette adresse ne ressemble pas à une adresse e-mail.";
}

/**
 * Les champs outilles, par la cle que leur donne le pave d'identite.
 *
 * Un champ absent de cette table n'a PAS de regle, et c'est un choix a chaque
 * fois — voir l'en-tete. Ne rien rendre est le comportement normal.
 */
const REGLES = Object.freeze({
  phone: messageTelephone,
  email: messageEmail,
  dob: messageDateNaissance,
  nir: messageNir,
  birthCityCode: messageCodeInsee,
  sex: messageSexe,
});

/**
 * Le message a montrer pour un champ, ou une chaine vide s'il n'y a rien a
 * dire. C'est le seul point d'entree des ecrans.
 */
export function messageFormat(champ, valeur) {
  const regle = REGLES[champ];
  if (!regle) return "";
  try {
    return regle(valeur) || "";
  } catch {
    // Un controle qui casse ne doit jamais emporter l'affichage du dossier.
    return "";
  }
}

export default messageFormat;

/**
 * Ponctue une date de naissance pendant qu'on la tape.
 *
 * Nicolas, 19/09 : « quand on saisit la ddn ce genre de message est vraiment
 * penible. Fais en sorte que s'il n'y a pas de slash alors le systeme les
 * ajoute », capture d'un « 17051986 » barre de rouge a l'appui.
 *
 * Il a raison, et le reproche porte sur le bon endroit : le controle etait
 * juste, c'est l'EXIGENCE qui etait mal placee. Personne ne tape les barres
 * obliques d'une date de naissance ; les reclamer, c'est faire corriger a la
 * main ce que la machine sait poser.
 *
 * La fonction ne fait QUE ponctuer. Elle ne complete pas, ne devine pas de
 * siecle, ne corrige aucun chiffre -- « 32/13/1980 » reste « 32/13/1980 » et
 * c'est `messageDateNaissance` qui le refusera. Une saisie qui se reecrit
 * toute seule sous les doigts est pire qu'un message.
 *
 * Elle est faite pour tourner a CHAQUE frappe : elle laisse donc passer les
 * etats intermediaires (« 17 », « 17/0 ») sans rien y ajouter de force.
 */
export function ponctuerDateNaissance(valeur) {
  /*
    CORRECTIF DU 19/09 (16h15), ET C'ETAIT MA FAUTE.

    Nicolas : « La date de naissance ne fonctionne pas. Entre le jour et le
    mois OK mais pas apres. »

    La premiere version commencait par `if (/[^0-9]/.test(brut)) return brut;`
    — « une date deja ponctuee, on n'y touche pas », pour ne pas remettre une
    barre oblique qu'on vient d'effacer. L'intention etait bonne, la
    consequence absurde : des que la PREMIERE barre etait posee, la chaine
    contenait un caractere non numerique, et la fonction rendait tout le reste
    tel quel. « 17/05 » marchait, « 17/051986 » restait « 17/051986 ».

    Et mes essais ne l'ont pas vu parce qu'ils ne nourrissaient la fonction
    qu'avec des chaines ENTIEREMENT numeriques : ils verifiaient la garde au
    lieu de verifier le comportement. Un essai qui ne joue que le chemin
    heureux atteste surtout de ce qu'on a imagine.

    On ne garde donc plus rien de la ponctuation d'entree : on retient les
    chiffres, et on repose les barres. Effacer marche quand meme, parce qu'on
    efface un CHIFFRE et pas un separateur — « 17/05 » devient « 17/0 », dont
    les chiffres sont « 170 », qui se reponctue en « 17/0 ». Et coller
    « 17-05-1986 » ou « 17.05.1986 » donne desormais la bonne date, ce que la
    version precedente refusait.
  */
  const chiffres = String(valeur ?? "").replace(/\D/g, "").slice(0, 8);
  if (chiffres.length <= 2) return chiffres;
  if (chiffres.length <= 4) return `${chiffres.slice(0, 2)}/${chiffres.slice(2)}`;
  return `${chiffres.slice(0, 2)}/${chiffres.slice(2, 4)}/${chiffres.slice(4)}`;
}
